/**
 * Module dependencies.
 */

import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { TourData } from 'src/types/tour';
import { forwardRef, useMemo, useState } from 'react';
import { ifProp } from 'styled-tools';
import { media } from 'src/styles/media';
import { normalizeNewLine } from 'src/core/utils/string';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInRightAnimation from 'src/components/core/animations/fade-in-right';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import RawHtml from 'src/components/core/raw-html';
import styled, { css } from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = Partial<TourData['sections']['tips']>;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: var(--color-yellow);
  position: relative;

  ${media.max.md`
    margin-bottom: 5.75rem;
    padding-bottom: 1.25rem;
    padding-top: 8.125rem;
  `}
`;

/**
 * `ContainerStyle` styled component.
 */

const ContainerStyle = styled(Container)`
  ${media.min.md`
    display: flex;
    height: 100%;
    padding: 0 4.5rem;
  `}

  ${media.min.xxl`
    display: flex;
    padding: 0 calc((100vw - var(--container-max-width)) / 2 + var(--gutter));
  `}
`;

/**
 * `FirstGrid` styled component.
 */

const FirstGrid = styled.div`
  ${media.min.md`
   display: grid;
   grid-template-areas:
     'titleWrapper titleWrapper'
     'description  .           '
     'description  .           ';
   grid-template-columns: 3fr 1fr;
   grid-template-rows: 1fr 2fr 5fr 2fr;
   padding: 9.8vw 0;
   width: 47vw;
 `}

  ${media.min.lg`
    padding: 7.3vw 0 5.4vw;
  `}

  ${media.min.xxl`
    width: 37vw;
  `}
`;

/**
 * `SecondGrid` styled component.
 */

const SecondGrid = styled.div`
  ${media.min.md`
    display: grid;
    grid-template-areas:
      '.    buttons'
      'card card';
    grid-template-columns: 3fr 4fr;
    grid-template-rows: max-content 1fr;
    padding: 9.8vw 0;
    width: 101vw;
  `}

  ${media.min.lg`
    padding: 8.3vw 0 5.4vw;
    width: 90vw;
  `}

  ${media.min.xl`
    padding: 6.8vw 0 5.4vw;
    width: 77vw;
  `}

  ${media.min.xxl`
    width: 56vw;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  position: relative;
  z-index: 1;

  ${media.min.md`
    grid-area: titleWrapper;
    grid-row-start: 2;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  color: var(--color-dark);
  margin: 0;
  position: relative;
  text-transform: uppercase;
  z-index: 2;

  ${media.max.md`
    margin-bottom: 4rem;
    text-align: center;
  `}
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Text)`
  color: var(--color-dark);
  margin-bottom: 1.5rem;
  position: relative;
  text-transform: uppercase;
  z-index: 2;

  ${media.max.md`
    margin-bottom: 1.25rem;
    text-align: center;
 `}
`;

/**
 * `TextHandWritten` styled component.
 */

const TextHandWritten = styled(Text)`
  color: var(--color-white);
  display: block;
  margin: 1rem 0 0;
  text-align: center;
  text-transform: none;

  ${media.min.md`
    margin: 0.25rem 0 0;
    text-align: left;
  `}

  @supports (-webkit-hyphens: none) {
    font-size: clamp(35px, ${clampCalc(35, 40)}, 40px);

    ${media.min.md`
      margin: 0;
      position: relative;
      top: -1rem;
    `}
  }
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  ${media.min.md`
    align-self: center;
    grid-area: description;
    grid-row-start: 3;
    margin: 2rem 0 0;
  `}
`;
/**
 * `Description` styled component.
 */

const Description = styled.p`
  color: var(--color-dark);
  font-family: var(--font-futura-pt);
  font-size: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 20)}, ${20 / baseFontSize}rem);
  letter-spacing: clamp(${0.4 / baseFontSize}rem, ${clampCalc(0.4, 0.5)}, ${0.5 / baseFontSize}rem);
  line-height: clamp(${25 / baseFontSize}rem, ${clampCalc(25, 30)}, ${30 / baseFontSize}rem);
  margin: 0 0 4.5rem;
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9375rem;
  justify-content: center;
  justify-self: end;
  margin-bottom: 2.625rem;

  ${media.min.md`
    grid-area: buttons;
    justify-content: space-between;
    margin-bottom: 1.125rem;
  `}

  ${media.min.xl`
    margin-bottom: 3.125rem;
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  overflow: hidden;
  position: relative;

  ${media.max.md`
    padding-bottom: 125.73%;
  `}
`;

/**
 * `TextWrapper` styled component.
 */

const TextWrapper = styled.div`
  height: 100%;

  ${media.max.md`
    padding: 2rem 1.25rem 3.5rem;
  `}

  ${media.min.md`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 2.375rem 1.5625rem;
  `}
`;

/**
 * `CardTitle` styled component.
 */

const CardTitle = styled(Text)`
  margin-bottom: 1.5625rem;
  position: relative;
  text-transform: uppercase;

  ${media.max.md`
    letter-spacing: 0.375rem;
    margin-bottom: 0.625rem;
  `}
`;

/**
 * `CardText` styled component.
 */

const CardText = styled(Text).attrs({
  as: 'p',
  variant: 'paragraph'
})`
  font-size: 0.9375rem;
  letter-spacing: 0.0238rem;
  line-height: 1.25rem;
  margin: 0;
  position: relative;
  text-transform: none;
`;

/**
 * `Author` styled component.
 */

const Author = styled(CardText)`
  margin: 1rem 0 0;
  text-align: right;
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)<{ active?: boolean }>`
  ${ifProp(
    'active',
    css`
      background-color: var(--color-dark);
      border-color: var(--color-dark);
      color: var(--color-white);
    `
  )}
`;

/**
 * `CardWrapper` styled component.
 */

const CardWrapper = styled.div`
  background-color: var(--color-white);
  color: var(--color-dark);

  &.animate-enter {
    opacity: 0;
  }

  &.animate-exit {
    opacity: 1;
  }

  &.animate-enter-active,
  &.animate-enter-done {
    opacity: 1;
  }

  &.animate-enter-active {
    transition: opacity var(--transition-default);
  }

  &.animate-exit-active {
    opacity: 0;
    transition: opacity var(--transition-default);
  }

  ${media.min.md`
   display: grid;
   grid-area: card;
   grid-template-columns: 4fr 3fr;
 `}

  ${media.min.lg`
   grid-template-columns: 2fr 3fr;
 `}

  ${media.min.xxl`
    grid-template-columns: 4fr 3fr;
  `}
`;

/**
 * `TipsSection` component.
 */

const TipsSection = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { firstTip, header, images, secondTip, subTitle, text, thirdTip, title } = props;

  const [tipType, setTipType] = useState<string>('first');
  const isMobile = useBreakpoint('md', 'max');

  const activeTip = useMemo(() => {
    if (tipType === 'first') {
      return {
        image: images?.first,
        text: firstTip?.text,
        title: firstTip?.title
      };
    }

    if (tipType === 'second') {
      return {
        image: images?.second,
        text: secondTip?.text,
        title: secondTip?.title
      };
    }

    return {
      image: images?.third,
      text: thirdTip?.text,
      title: thirdTip?.title
    };
  }, [
    firstTip?.text,
    firstTip?.title,
    images?.first,
    images?.second,
    images?.third,
    secondTip?.text,
    secondTip?.title,
    thirdTip?.text,
    thirdTip?.title,
    tipType
  ]);

  return (
    <Wrapper ref={ref}>
      <ContainerStyle fluid>
        <FirstGrid>
          <TitleWrapper>
            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <SubTitle as={'p'} variant={'header'}>
                  {header}
                </SubTitle>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <SubTitle as={'p'} variant={'header'}>
                  {header}
                </SubTitle>
              </FadeInUpAnimation>
            )}

            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <Title as={'h2'} variant={'boldM'}>
                  {title}

                  <TextHandWritten as={'span'} variant={'handwrittenS'}>
                    {subTitle?.replaceAll('-', '-')}
                  </TextHandWritten>
                </Title>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Title as={'h2'} variant={'boldM'}>
                  {title}

                  <TextHandWritten as={'span'} variant={'handwrittenS'}>
                    {subTitle}
                  </TextHandWritten>
                </Title>
              </FadeInUpAnimation>
            )}
          </TitleWrapper>

          <DescriptionWrapper>
            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <Description>{text}</Description>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Description>{text}</Description>
              </FadeInUpAnimation>
            )}
          </DescriptionWrapper>
        </FirstGrid>

        <SecondGrid>
          <ButtonWrapper>
            <FadeInAnimation options={{ transitionTime: 1 }}>
              <StyledButton
                active={tipType === 'first'}
                colorTheme={'secondary'}
                onClick={() => setTipType('first')}
                variant={'outlined'}
              >
                {firstTip?.button}
              </StyledButton>
            </FadeInAnimation>

            <FadeInAnimation options={{ transitionTime: 1 }}>
              <StyledButton
                active={tipType === 'second'}
                colorTheme={'secondary'}
                onClick={() => setTipType('second')}
                variant={'outlined'}
              >
                {secondTip?.button}
              </StyledButton>
            </FadeInAnimation>

            <FadeInAnimation options={{ transitionTime: 1 }}>
              <StyledButton
                active={tipType === 'third'}
                colorTheme={'secondary'}
                onClick={() => setTipType('third')}
                variant={'outlined'}
              >
                {thirdTip?.button}
              </StyledButton>
            </FadeInAnimation>
          </ButtonWrapper>

          <SwitchTransition>
            <CSSTransition
              addEndListener={(node, done) => {
                node.addEventListener('transitionend', done, false);
              }}
              classNames={'animate'}
              key={tipType}
            >
              <CardWrapper>
                <ImageWrapper>
                  {activeTip?.image && (
                    <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
                      <Image alt={activeTip?.title || ''} layout={'fill'} objectFit={'cover'} src={activeTip?.image} />
                    </FadeInAnimation>
                  )}
                </ImageWrapper>

                <FadeInAnimation options={{ transitionTime: 1 }}>
                  <TextWrapper>
                    <CardTitle as={'h2'} variant={'titleXS'}>
                      {activeTip?.title}
                    </CardTitle>

                    <RawHtml element={CardText}>{normalizeNewLine(activeTip?.text)}</RawHtml>

                    <Author>{'José Luís Peixoto'}</Author>
                  </TextWrapper>
                </FadeInAnimation>
              </CardWrapper>
            </CSSTransition>
          </SwitchTransition>
        </SecondGrid>
      </ContainerStyle>
    </Wrapper>
  );
});

TipsSection.displayName = 'TipsSection';

/**
 * Export `TipsSection` component.
 */

export default TipsSection;
